/**
 * Valida se um reforço mensal de cofre cabe no tecto do grupo do método.
 * Alinhado com ``validate_pot_monthly_against_method_group`` no backend.
 */
import { ALLOCATION_TYPES } from './constants';

const NEEDS_KEYWORDS = ['necess', 'needs', 'essencial'];
const WANTS_KEYWORDS = ['desej', 'estilo', 'want', 'prazer'];
const SAVINGS_KEYWORDS = ['poupan', 'invest', 'savings'];

/**
 * @param {Array<object>|undefined} groups
 * @param {'essential'|'lifestyle'|'personal_investment'} allocation
 * @returns {object|null}
 */
export function findBudgetGroupForAllocation(groups, allocation) {
  if (!groups?.length) return null;

  if (allocation === ALLOCATION_TYPES.PERSONAL_INVESTMENT) {
    return (
      groups.find((g) => g.is_pay_yourself_first) ||
      groups.find((g) =>
        SAVINGS_KEYWORDS.some((kw) => (g.name || '').toLowerCase().includes(kw)),
      ) ||
      null
    );
  }

  if (allocation === ALLOCATION_TYPES.ESSENTIAL) {
    return (
      groups.find((g) =>
        NEEDS_KEYWORDS.some((kw) => (g.name || '').toLowerCase().includes(kw)),
      ) ||
      groups.find(
        (g) => !g.is_pay_yourself_first && Number(g.target_pct) >= 40,
      ) ||
      null
    );
  }

  if (allocation === ALLOCATION_TYPES.LIFESTYLE) {
    return (
      groups.find((g) =>
        WANTS_KEYWORDS.some((kw) => (g.name || '').toLowerCase().includes(kw)),
      ) ||
      groups.find(
        (g) =>
          !g.is_pay_yourself_first &&
          Number(g.target_pct) > 20 &&
          Number(g.target_pct) < 40,
      ) ||
      null
    );
  }

  return null;
}

/**
 * @param {object} group
 * @param {{ excludePotMonthly?: number }} [options]
 */
export function getGroupPlannedRemaining(group, options = {}) {
  const target = Number(group?.target_budget_eur);
  if (!Number.isFinite(target) || target <= 0) return null;

  const allocated = Number(group.categories_budget_sum_eur ?? 0);
  const exclude = Number(options.excludePotMonthly ?? 0);
  const other = Math.max(0, allocated - (Number.isFinite(exclude) ? exclude : 0));
  return Math.max(0, target - other);
}

/**
 * @param {{
 *   groups: Array<object>|undefined,
 *   allocation: string,
 *   monthly: number|null,
 *   excludePotMonthly?: number,
 * }} params
 */
export function evaluatePotMonthlyAgainstGroup({
  groups,
  allocation,
  monthly,
  excludePotMonthly = 0,
}) {
  if (monthly == null || !Number.isFinite(monthly) || monthly <= 0) {
    return { fits: true, isInvestmentGroup: false, group: null };
  }

  const group = findBudgetGroupForAllocation(groups, allocation);
  if (!group) {
    return { fits: true, isInvestmentGroup: false, group: null };
  }

  if (group.is_pay_yourself_first) {
    return { fits: true, isInvestmentGroup: true, group };
  }

  const target = Number(group.target_budget_eur);
  const allocated = Number(group.categories_budget_sum_eur ?? 0);
  const remaining = getGroupPlannedRemaining(group, { excludePotMonthly });

  if (remaining == null) {
    return { fits: true, isInvestmentGroup: false, group };
  }

  return {
    fits: monthly <= remaining + 0.001,
    isInvestmentGroup: false,
    group,
    target,
    allocated: Math.max(0, allocated - (excludePotMonthly || 0)),
    remaining,
    groupName: group.name,
  };
}

/**
 * Aviso calmo (não bloqueia) vs headroom da Poupança — inclui grupos PYF.
 * Usado no wizard SMART; distinto de evaluatePotMonthlyAgainstGroup (hard).
 *
 * @param {{
 *   groups: Array<object>|undefined,
 *   monthly: number|null,
 *   cycles?: number,
 *   excludePotMonthly?: number,
 * }} params
 */
export function evaluateGoalSavingsSoftWarning({
  groups,
  monthly,
  cycles = 1,
  excludePotMonthly = 0,
}) {
  if (monthly == null || !Number.isFinite(monthly) || monthly <= 0) {
    return { over: false, group: null };
  }

  const group = findBudgetGroupForAllocation(groups, 'personal_investment');
  if (!group) {
    return { over: false, group: null };
  }

  const remaining = getGroupPlannedRemaining(group, { excludePotMonthly });
  if (remaining == null) {
    return { over: false, group };
  }

  const cycleCount = Math.max(1, Number(cycles) || 1);
  const over = monthly > remaining + 0.001;
  const target = Number(group.target_budget_eur);
  const allocated = Number(group.categories_budget_sum_eur ?? 0);

  return {
    over,
    group,
    groupName: group.name,
    remaining,
    target,
    allocated: Math.max(0, allocated - (excludePotMonthly || 0)),
    maxComfortableTarget: remaining * cycleCount,
    cycles: cycleCount,
  };
}
